import React, { useState, useEffect } from 'react';
import { 
    Text, 
    Flex, 
    Button, 
    Box, 
    Select,
    IconButton,
    VStack
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';

const FIELD_OPTIONS = [
    { value: 'name', label: 'Name', description: "The caller's full name." },
    { value: 'email', label: 'Email', description: "The caller's email for confirmation." },
    { value: 'phone', label: 'Phone', description: "The caller's phone number." },
    { value: 'address', label: 'Address', description: "The caller's address." },
    { value: 'reason_for_appointment', label: 'Reason for appointment', description: 'A brief reason of why they are calling.' }
];

const DEFAULT_FIELDS = [
    { name: 'name', description: "The caller's full name." },
    { name: 'email', description: "The caller's email for the appointment." }
];

const CustomVariables = ({ customVariables: propCustomVariables, onSave }) => {
    const [customVariables, setCustomVariables] = useState([]);
    const [selectedFieldType, setSelectedFieldType] = useState('');
    const [newFieldDescription, setNewFieldDescription] = useState('');

    useEffect(() => {
        if (propCustomVariables && propCustomVariables.length > 0) {
            setCustomVariables(propCustomVariables);
        } else {
            setCustomVariables(DEFAULT_FIELDS);
        }
    }, [propCustomVariables]);

    const getUsedFieldTypes = () => {
        return customVariables.map(field => field.name);
    };

    const getAvailableFieldOptions = () => {
        const usedFields = getUsedFieldTypes();
        return FIELD_OPTIONS.filter(option => !usedFields.includes(option.value));
    };

    const handleFieldTypeChange = (event) => {
        const selectedValue = event.target.value;
        setSelectedFieldType(selectedValue);
        
        const selectedOption = FIELD_OPTIONS.find(option => option.value === selectedValue);
        if (selectedOption) {
            setNewFieldDescription(selectedOption.description);
        }
    };

    const handleAddField = () => {
        if (!selectedFieldType || !newFieldDescription.trim()) return;

        const newField = {
            name: selectedFieldType,
            description: newFieldDescription.trim()
        };

        const updatedVariables = [...customVariables, newField];
        setCustomVariables(updatedVariables);
        
        // Update settingsData
        onSave(updatedVariables);

        // Reset form
        setSelectedFieldType('');
        setNewFieldDescription('');
    };

    const handleRemoveField = (indexToRemove) => {
        const updatedVariables = customVariables.filter((_, index) => index !== indexToRemove);
        setCustomVariables(updatedVariables);
        
        // Update settingsData
        onSave(updatedVariables);
    };

    const availableOptions = getAvailableFieldOptions();
    const canAddField = selectedFieldType && newFieldDescription.trim() && availableOptions.length > 0;

    return (
        <Box>
            <Text fontSize="lg" fontWeight="semibold" mb={2}>
                Required fields for booking
            </Text>
            <Text color="gray.600" mb={4}>
                Define what information must be collected in order to schedule a meeting.
            </Text>

            <VStack spacing={3} align="stretch">
                {customVariables.map((field, index) => {
                    const fieldOption = FIELD_OPTIONS.find(opt => opt.value === field.name);
                    const fieldLabel = fieldOption ? fieldOption.label : field.name;
                    const isNameField = field.name === 'name';
                    return (
                        <Flex key={index} align="center" gap={3}>
                            <Box flex="0 0 120px">
                                <Text fontWeight="medium">{fieldLabel}</Text>
                            </Box>
                            <IconButton
                                icon={<CloseIcon />}
                                size="sm"
                                variant="ghost"
                                aria-label="Remove field"
                                onClick={() => handleRemoveField(index)}
                                _hover={{ bg: 'gray.100' }}
                                isDisabled={isNameField}
                                style={isNameField ? { visibility: 'hidden' } : {}}
                            />
                            <Box flex="1">
                                <Text fontSize="sm" color="gray.600" minH="32px" py={1}>
                                    {field.description}
                                </Text>
                            </Box>
                        </Flex>
                    );
                })}

                {/* Add new field section */}
                {availableOptions.length > 0 && (
                    <Flex align="center" gap={3}>
                        <Box flex="0 0 120px">
                            <Select
                                placeholder="New field type"
                                value={selectedFieldType}
                                onChange={handleFieldTypeChange}
                                size="sm"
                            >
                                {availableOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                        </Box>
                        <Box flex="1">
                            <Text fontSize="sm" color="gray.600" minH="32px" py={1}>
                                {newFieldDescription}
                            </Text>
                        </Box>
                        <Button
                            size="sm"
                            colorScheme="blue"
                            onClick={handleAddField}
                            isDisabled={!canAddField}
                        >
                            Add
                        </Button>
                    </Flex>
                )}
            </VStack>
        </Box>
    );
};

export default CustomVariables;
